/**
 * Calculator view — interactive fee calculator. Inputs on the left,
 * live results on the right. The "Generate Fee Proposal" button at
 * the bottom hands the inputs + result to the parent, which switches
 * the FeeCalculator into Proposal mode.
 *
 * Ported from optimum-fees-calculator.jsx (Calculator component).
 */

import React, { useState, useMemo } from "react";
import { C } from "./brand";
import {
  calculateFees,
  fmtZAR,
  fmtPct,
  SPECIAL_FUNDS,
  TRANSFER_TYPES,
  PRODUCT_OPTIONS,
} from "./feeEngine";
import {
  Field,
  NumberInput,
  RadioGroup,
  CheckboxToggle,
  SubLabel,
  ResultCard,
  KV,
  Divider,
  Empty,
  CapWarning,
  inputBase,
} from "./primitives";

export default function Calculator({ settings, onGenerateProposal }) {
  const [hasLumpSum, setHasLumpSum] = useState(false);
  const [hasRecurring, setHasRecurring] = useState(false);
  const [lumpSumAmount, setLumpSumAmount] = useState(null);
  const [monthlyAmount, setMonthlyAmount] = useState(null);
  const [transferType, setTransferType] = useState(TRANSFER_TYPES.NONE);
  const [specialFund, setSpecialFund] = useState(SPECIAL_FUNDS.NONE);
  const [hours, setHours] = useState(null);
  const [splitPct, setSplitPct] = useState(70);
  const [clientName, setClientName] = useState("");
  const [productName, setProductName] = useState("");

  const result = useMemo(
    () =>
      calculateFees(
        { hasLumpSum, hasRecurring, lumpSumAmount, monthlyAmount, hours, splitPct, specialFund, transferType },
        settings
      ),
    [hasLumpSum, hasRecurring, lumpSumAmount, monthlyAmount, hours, splitPct, specialFund, transferType, settings]
  );

  const anySelected = hasLumpSum || hasRecurring;
  const isExempt = transferType !== TRANSFER_TYPES.NONE;
  const lumpQualifies = hasLumpSum && lumpSumAmount !== null && lumpSumAmount >= settings.minThreshold;
  const showSpecialFund = anySelected;
  const showTransferType = hasLumpSum;
  const showHours = lumpQualifies && !isExempt;
  const showSplit = anySelected;

  // Dynamic step numbering — only count visible steps
  let n = 0;
  const step = () => ++n;
  const stepLabels = {
    type: step(),
    amounts: anySelected ? step() : null,
    transfer: showTransferType ? step() : null,
    portfolio: showSpecialFund ? step() : null,
    hours: showHours ? step() : null,
    split: showSplit ? step() : null,
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {/* ===== Inputs column ===== */}
      <div>
        <h2 style={{ color: C.tealDark, fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Calculate fees</h2>
        <p style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>
          Step through the questions below. Results update live on the right.
        </p>

        {/* Client details — shown at top, used by the proposal */}
        <div
          style={{
            marginBottom: 24,
            padding: 16,
            border: `1px dashed ${C.border}`,
            borderRadius: 4,
            background: C.bgSoft,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: C.teal, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 12 }}>
            Client details
          </div>
          <Field label="Client name">
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="e.g. John Smith"
              style={inputBase}
              onFocus={(e) => (e.target.style.borderColor = C.tealMid)}
              onBlur={(e) => (e.target.style.borderColor = C.border)}
            />
          </Field>
          <Field label="Investment description">
            <select
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              style={{
                ...inputBase,
                appearance: "none",
                paddingRight: 36,
                cursor: "pointer",
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3e%3cpath fill='none' stroke='%23595959' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M1 1.5l5 5 5-5'/%3e%3c/svg%3e")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
              }}
              onFocus={(e) => (e.target.style.borderColor = C.tealMid)}
              onBlur={(e) => (e.target.style.borderColor = C.border)}
            >
              <option value="">Select a product…</option>
              {PRODUCT_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field
          label={`${stepLabels.type} — Investment components`}
          hint="Select one or both. An investment can be a once-off, a debit order, or both combined."
        >
          <CheckboxToggle
            checked={hasLumpSum}
            onChange={setHasLumpSum}
            label="Lump sum"
            hint={`Once-off contribution. Initial fee applies if ≥ ${fmtZAR(settings.minThreshold)}.`}
          />
          <CheckboxToggle
            checked={hasRecurring}
            onChange={setHasRecurring}
            label="Recurring contribution"
            hint="Monthly debit order. No initial fee charged on recurring portion."
          />
        </Field>

        {anySelected && (
          <Field label={`${stepLabels.amounts} — Amounts`}>
            {hasLumpSum && (
              <div style={{ marginBottom: hasRecurring ? 12 : 0 }}>
                <SubLabel>Lump sum amount</SubLabel>
                <NumberInput
                  value={lumpSumAmount}
                  onChange={setLumpSumAmount}
                  prefix="R"
                  placeholder="0"
                  format="currency"
                />
                {lumpSumAmount !== null && lumpSumAmount < settings.minThreshold && (
                  <div style={{ fontSize: 11, color: C.orange, marginTop: 4, fontWeight: 500 }}>
                    Below threshold ({fmtZAR(settings.minThreshold)}) — no initial fee will apply.
                  </div>
                )}
              </div>
            )}
            {hasRecurring && (
              <div>
                <SubLabel>Monthly contribution</SubLabel>
                <NumberInput
                  value={monthlyAmount}
                  onChange={setMonthlyAmount}
                  prefix="R"
                  suffix="/ month"
                  placeholder="0"
                  format="currency"
                />
              </div>
            )}
          </Field>
        )}

        {showTransferType && (
          <Field
            label={`${stepLabels.transfer} — Transaction type`}
            hint="Initial fee only applies when new money is invested above the threshold."
          >
            <RadioGroup
              value={transferType}
              onChange={setTransferType}
              options={[
                { value: TRANSFER_TYPES.NONE, label: "New investment", hint: "Standard initial fee logic applies" },
                { value: TRANSFER_TYPES.SECTION14, label: "Section 14 transfer", hint: "Retirement Annuity transfer — Pension Funds Act" },
                { value: TRANSFER_TYPES.DIRECTIVE135, label: "Directive 135 transfer", hint: "Living Annuity transfer — FSCA Directive 135" },
                { value: TRANSFER_TYPES.EXISTING, label: "Appointment on existing investment", hint: "No new money invested — ongoing fee only" },
              ]}
            />
          </Field>
        )}

        {showSpecialFund && (
          <Field
            label={`${stepLabels.portfolio} — Portfolio composition`}
            hint="Special rate only applies if invested 100% in one of the listed products."
          >
            <RadioGroup
              value={specialFund}
              onChange={setSpecialFund}
              options={[
                { value: SPECIAL_FUNDS.NONE, label: "Mixed portfolio / other funds" },
                { value: SPECIAL_FUNDS.INCOME, label: "100% — Optimum BCI Income Fund", hint: "Ongoing 0.10% + VAT" },
                { value: SPECIAL_FUNDS.EDGE, label: "100% — OIG Capital Edge Model Portfolio", hint: "Ongoing 0.15% + VAT" },
              ]}
            />
          </Field>
        )}

        {showHours && (
          <Field
            label={`${stepLabels.hours} — Hours billed`}
            hint={`Hourly rate: ${fmtZAR(settings.hourlyRate)} (editable in Settings)`}
          >
            <NumberInput value={hours} onChange={setHours} suffix="hrs" placeholder="0" min={0} step={0.25} />
          </Field>
        )}

        {showSplit && (
          <Field
            label={`${stepLabels.split} — Your split %`}
            hint={
              showHours
                ? "Used to back-solve initial fee and to determine ongoing rate."
                : "Used to determine the ongoing fee rate."
            }
          >
            <NumberInput value={splitPct} onChange={setSplitPct} suffix="%" placeholder="70" min={1} step={1} />
          </Field>
        )}
      </div>

      {/* ===== Results column ===== */}
      <div>
        <h2 style={{ color: C.tealDark, fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Results</h2>
        <p style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>
          Live calculation. All figures shown excl. VAT unless stated.
        </p>

        {/* Initial fee card */}
        <ResultCard title="Initial fee" accent={result.initial.applies}>
          {!result.initial.applies ? (
            <Empty msg={result.initial.reason} />
          ) : (
            <>
              <KV
                label="Fee % of lump sum"
                value={fmtPct(result.initial.gross / (result.initial.lumpSumAmount || 1), 2)}
                bold
              />
              <KV label="Subtotal (excl. VAT)" value={fmtZAR(result.initial.gross)} bold />
              <KV label={`VAT (${fmtPct(settings.vatRate, 0)})`} value={fmtZAR(result.initial.vat)} />
              <KV label="Total charged to client" value={fmtZAR(result.initial.total)} highlight />
              <Divider />
              <KV label="Your take-home" value={fmtZAR(result.initial.takeHome)} bold />
              {result.initial.capped && (
                <CapWarning
                  target={result.initial.targetEarnings}
                  actual={result.initial.takeHome}
                  capPct={fmtPct(settings.feeCap, 0)}
                />
              )}
              <div style={{ fontSize: 11, color: C.muted, marginTop: 12, lineHeight: 1.5 }}>{result.initial.reason}</div>
            </>
          )}
        </ResultCard>

        {/* Ongoing fee card */}
        <ResultCard title="Ongoing fee" accent={result.ongoing.rate > 0}>
          {result.ongoing.rate === 0 ? (
            <Empty msg={result.ongoing.reason} />
          ) : (
            <>
              <KV label="Rate" value={`${result.ongoing.display} p.a. + VAT`} bold />
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4, marginBottom: 12, lineHeight: 1.5 }}>
                {result.ongoing.reason}
              </div>
              {result.ongoing.annualTotal !== undefined && (
                <>
                  <Divider />
                  {result.ongoing.combined && (
                    <KV
                      label="Year-1 base"
                      value={fmtZAR(result.ongoing.base)}
                    />
                  )}
                  <KV label="Annual estimate (excl. VAT)" value={fmtZAR(result.ongoing.annualGross)} />
                  <KV label={`VAT (${fmtPct(settings.vatRate, 0)})`} value={fmtZAR(result.ongoing.annualVat)} />
                  <KV label="Annual total" value={fmtZAR(result.ongoing.annualTotal)} highlight />
                  <KV label="Monthly equivalent" value={fmtZAR(result.ongoing.monthlyTotal)} />
                  <Divider />
                  <KV label="Your portion (annual)" value={fmtZAR(result.ongoing.takeHomeAnnual)} bold />
                  <KV label="Your portion (monthly)" value={fmtZAR(result.ongoing.takeHomeMonthly)} />
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
                    Annual estimate × {splitPct}% split (VAT excluded — paid to SARS).
                  </div>
                  {result.ongoing.combined && (
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 6, fontStyle: "italic", lineHeight: 1.5 }}>
                      Year-1 base = lump sum + (monthly × 12). No growth assumed.
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </ResultCard>
      </div>
    </div>

    {(result.initial.applies || result.ongoing.rate > 0) && (
      <div style={{ marginTop: 32, display: "flex", justifyContent: "center" }}>
        <button
          onClick={() =>
            onGenerateProposal({
              inputs: { hasLumpSum, hasRecurring, lumpSumAmount, monthlyAmount, hours, splitPct, specialFund, transferType },
              result,
              clientName,
              productName,
            })
          }
          style={{
            background: C.tealDark,
            color: C.white,
            border: "none",
            padding: "14px 32px",
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            letterSpacing: 0.3,
            minWidth: 280,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = C.teal)}
          onMouseLeave={(e) => (e.currentTarget.style.background = C.tealDark)}
        >
          Generate Fee Proposal →
        </button>
      </div>
    )}
    </>
  );
}
